/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef, useMemo} from 'react'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {
    Box,
    Heading,
    Text,
    Stack,
    Flex,
    Button,
    Grid,
    AspectRatio,
    Img,
    Skeleton,
    useMultiStyleConfig,
    useBreakpointValue,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Tabs, TabList, TabPanels, TabIndicator, Tab, TabPanel, IconButton,Progress} from '@chakra-ui/react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import Link from '@salesforce/retail-react-app/app/components/link'
import {LeftArrow} from '../../components/icons'
import PropTypes from 'prop-types'
import {useCustomerOrders} from '../../hooks/use-customer-orders'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useHistory} from 'react-router-dom'
import {InfoCircleFilled} from '../../components/icons'
import {getStatusConfig, StatusDot} from '../../utils/orderStatusConfig'
import {formatOrderDate} from '../../utils/formateDate'

const EmptyOrderMessage = ({showContinueShopping = true, storeOrders, onlineOrders}) => {
    const history = useHistory()
    const styles = useMultiStyleConfig('OrderHistory')
    const combinedStyles = {
        ...styles.noOrdersActionButton,
        ...styles.noOrdersActionButtonDesktop
    }

    return (
        <Stack
            data-testid="account-order-history-place-holder"
            mt={storeOrders.length > 0 || onlineOrders.length > 0 ? '24px' : ''}
        >
            <Flex {...styles.noOrderTitleContainer}>
                <IconButton
                    variant="unstyled"
                    h={'auto'}
                    minW={'auto'}
                    lineHeight={'0'}
                    icon={<InfoCircleFilled boxSize="16px" />}
                />
                <Heading {...styles.noOrderYetHeading}>
                    <FormattedMessage id="account_order.heading" defaultMessage="No Orders Yet" />
                </Heading>
            </Flex>
            <Flex {...styles.noOrdersContentContainer}>
                <Text {...styles.noOrdersDescription}>
                    <FormattedMessage
                        id="account_order.empty.text"
                        defaultMessage="We don't have any past or current orders to display for you."
                    />
                    {showContinueShopping && (
                        <Text as="span" {...styles.noOrdersActionMobile}>
                            <Button
                                onClick={() => history.push('/')}
                                {...styles.noOrdersActionButton}
                            >
                                <FormattedMessage
                                    id="no_order_yet.link.continue_shopping"
                                    defaultMessage="Continue Shopping."
                                />
                            </Button>
                        </Text>
                    )}
                </Text>
                {showContinueShopping && (
                    <Button onClick={() => history.push('/')} {...combinedStyles}>
                        <FormattedMessage
                            id="no_order_yet.link.continue_shopping"
                            defaultMessage="Continue Shopping."
                        />
                    </Button>
                )}
            </Flex>
        </Stack>
    )
}

const SkeletonPlaceholder = () =>
    Array.from({length: 4}).map((_, i) => (
        <Stack key={i} spacing={4} mb={4}>
            <Stack spacing={2}>
                <Skeleton h="20px" w="112px" />
                <Skeleton h="20px" w="200px" />
            </Stack>
            <Grid templateColumns="repeat(4, 140px)" gap={2} overflow="hidden">
                {Array.from({length: 4}).map((_, i) => (
                    <AspectRatio key={i} ratio={2 / 3} overflow="hidden">
                        <Skeleton boxSize="full" />
                    </AspectRatio>
                ))}
            </Grid>
            <Skeleton h="20px" w="200px" />
        </Stack>
    ))

// CSS-only responsive carousel constants
const GAP_SIZE = 8 // px
const SLIDES_MOBILE = 2.5
const SLIDES_DESKTOP = 6.5

// Responsive item widths calculated with CSS calc()
// Mobile: (100% - gaps) / 2.5 slides
// Desktop: (100% - gaps) / 6.5 slides
const ITEM_WIDTH_MOBILE = `calc((100% - ${(Math.floor(SLIDES_MOBILE) - 1) * GAP_SIZE}px) / ${SLIDES_MOBILE})`
const ITEM_WIDTH_DESKTOP = `calc((100% - ${(Math.floor(SLIDES_DESKTOP) - 1) * GAP_SIZE}px) / ${SLIDES_DESKTOP})`

const OrderProductImages = ({orderItems, isLoading, orderId, orderNo}) => {
    // CSS-only horizontal scroll carousel - no Swiper/useBreakpointValue to avoid SSR/hydration issues
    // Responsive fixed thumbnail grid with "+N" overflow indicator.
    // Displays limited thumbnails based on breakpoint:
    // Mobile (base): max 3 slots → 2 thumbnails + "+N"
    // Desktop (md+): max 5 slots → 4 thumbnails + "+N"
    // Uses useBreakpointValue with fallback to ensure SSR-safe rendering.

    const maxSlots = useBreakpointValue(
        {
            base: 3,
            md: 5
        },
        {
            fallback: 'md'
        }
    )

    const visibleThumbs = useBreakpointValue(
        {
            base: 2,
            md: 4
        },
        {
            fallback: 'md'
        }
    )

    if (!maxSlots || !visibleThumbs) return null

    if (isLoading) {
        return (
            <Flex gap={2} overflow="hidden">
                {Array.from({length: orderItems?.length || 3}, (_, i) => (
                    <Skeleton key={i} h="204px" w="136px" flexShrink={0} />
                ))}
            </Flex>
        )
    }

    const styles = useMultiStyleConfig('OrderHistory')
    const totalItems = orderItems?.length || 0
    let itemsToRender = []
    let remainingCount = 0

    if (totalItems > maxSlots) {
        itemsToRender = orderItems.slice(0, visibleThumbs)
        remainingCount = totalItems - visibleThumbs
    } else {
        itemsToRender = orderItems
    }

    return (
        <Grid templateColumns={`repeat(${maxSlots}, 1fr)`} gap={2}>
            {itemsToRender?.map(({sku, productImageURL}, index) => (
                <Link key={`${sku}-${index}`} to={`/account/orders/${orderNo}`}>
                    <AspectRatio ratio={2 / 3}>
                        <Img
                            alt={`Product ${sku}`}
                            src={productImageURL}
                            fallback={<Box background="gray.100" boxSize="full" />}
                        />
                    </AspectRatio>
                </Link>
            ))}
            {remainingCount > 0 && (
                <AspectRatio ratio={2 / 3}>
                    <Box {...styles.remainingCountBox}>+{remainingCount}</Box>
                </AspectRatio>
            )}
        </Grid>
    )
}

OrderProductImages.propTypes = {
    orderItems: PropTypes.array,
    isLoading: PropTypes.bool
}

const AccountOrderHistory = () => {
    // Client-only rendering - skip SSR entirely to avoid hydration mismatches
    const [hasMounted, setHasMounted] = useState(false)
    useEffect(() => {
        setHasMounted(true)
    }, [])

    const styles = useMultiStyleConfig('OrderHistory')
    const {currency} = useCurrency()
    const {data: customer} = useCurrentCustomer()
    const {customerId} = customer
    const [activeTab, setActiveTab] = useState(0)
    const isMobile = useBreakpointValue({base: true, md: false})

    // Fetch both order types independently to avoid race conditions when switching tabs
    // Only fetch on client after mount
    const {
        data: onlineData,
        fetchNextPage: fetchNextOnline,
        isLoading: isLoadingOnline,
        isFetchingNextPage: isFetchingNextOnline,
        hasNextPage: hasNextOnline
    } = useCustomerOrders('online', hasMounted ? customerId : undefined)

    const {
        data: storeData,
        fetchNextPage: fetchNextStore,
        isLoading: isLoadingStore,
        isFetchingNextPage: isFetchingNextStore,
        hasNextPage: hasNextStore
    } = useCustomerOrders('store', hasMounted ? customerId : undefined)

    const onlineOrders = useMemo(
        () => onlineData?.pages?.flatMap(({orders = []}) => orders) ?? [],
        [onlineData?.pages]
    )

    const storeOrders = useMemo(
        () => storeData?.pages?.flatMap(({orders = []}) => orders) ?? [],
        [storeData?.pages]
    )

    const totalCounts = useMemo(
        () => ({
            online: onlineData?.pages[0]?.onlineOrdersLength || 0,
            store: onlineData?.pages[0]?.storeOrdersLength || 0
        }),
        [onlineData?.pages]
    )

    // Always show tab list - CSS handles responsive layout
    const showTabList = isLoadingOnline || !!totalCounts

    const headingRef = useRef()
    useEffect(() => {
        headingRef?.current?.focus()
    }, [])

    // Extracted render logic to avoid code duplication between tabs
    // Each tab passes its own data and pagination handlers
    const renderOrdersList = (
        ordersList,
        tabIsLoading,
        totalCount,
        tabFetchNextPage,
        tabIsFetchingNextPage,
        tabHasNextPage
    ) => {
        if (!ordersList?.length) {
            return null
        }

        const loadedCount = ordersList.length
        const shouldShowPagination = totalCount > 8

        return (
            <>
                {ordersList?.map(
                    ({
                        orderId,
                        orderNo,
                        orderDate,
                        orderStatus,
                        items: orderItems,
                        orderTotal,
                        isOnlineOrder,
                        storeInfo,
                        qtyProducts
                    }) => {
                        const statusConfig = getStatusConfig(orderStatus)

                        return (
                            <Stack key={orderId} {...styles.ordersCardContainer}>
                                <Flex w={'full'} justifyContent={'space-between'}>
                                    <Link
                                        to={`/account/orders/${orderNo}`}
                                        width="fit-content"
                                        _hover={{textDecoration: 'none'}}
                                    >
                                        <Stack {...styles.orderInfoContainer}>
                                            <Flex {...styles.orderStatusText} gap={2}>
                                                <StatusDot
                                                    color={statusConfig.color}
                                                    animated={statusConfig.animated}
                                                />
                                                <Text>{orderStatus}</Text>
                                            </Flex>
                                            <Text {...styles.orderPlacedDate}>
                                                {formatOrderDate(orderDate)}
                                            </Text>
                                            <Text {...styles.orderTotalWithItemCount}>
                                                <FormattedNumber
                                                    style="currency"
                                                    currency={currency}
                                                    value={orderTotal}
                                                />
                                                <Text as="span" ml="2px">
                                                    (
                                                    <FormattedMessage
                                                        defaultMessage="{count, plural, one {# item} other {# items}}"
                                                        id="account_order_history.label.num_of_items"
                                                        description="Number of items in order"
                                                        values={{
                                                            count: qtyProducts || orderItems?.length
                                                        }}
                                                    />
                                                    )
                                                </Text>
                                            </Text>
                                            {!isOnlineOrder && storeInfo && (
                                                <Text fontSize="md">{storeInfo.name}</Text>
                                            )}
                                        </Stack>
                                    </Link>
                                    <Button
                                        as={Link}
                                        to={`/account/orders/${orderNo}`}
                                        variant="link"
                                        {...styles.manageOrderButton}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Manage Order"
                                            id="account_order_history.link.manage_order"
                                        />
                                    </Button>
                                </Flex>
                                <OrderProductImages
                                    isLoading={tabIsLoading}
                                    orderItems={orderItems}
                                    orderId={orderId}
                                    orderNo={orderNo}
                                />
                            </Stack>
                        )
                    }
                )}
                {/* Show More Section */}
                {shouldShowPagination && (
                    <Box {...styles.showMoreSection}>
                        <Stack {...styles.progressBarContainer}>
                            <Text {...styles.showOrdersText}>
                                <FormattedMessage
                                    id="account_order_history.text.showing_orders"
                                    defaultMessage="Showing <b>{loadedCount}</b> of <b>{totalCount}</b> orders"
                                    values={{
                                        loadedCount,
                                        totalCount,
                                        b: (chunks) => (
                                            <Text as="span" {...styles.showOrdersTextBold}>
                                                {chunks}
                                            </Text>
                                        )
                                    }}
                                />
                            </Text>
                            <Progress
                                value={(loadedCount / totalCount) * 100}
                                {...styles.progressBar}
                            />
                            {tabHasNextPage && (
                                <Button
                                    isLoading={tabIsLoading || tabIsFetchingNextPage}
                                    variant="solid"
                                    onClick={() => tabFetchNextPage()}
                                    {...styles.showMoreButton}
                                >
                                    <FormattedMessage
                                        id="account_order_history.show_more"
                                        defaultMessage="Show More"
                                    />
                                </Button>
                            )}
                        </Stack>
                    </Box>
                )}
            </>
        )
    }

    // SSR: render skeleton placeholder, no dynamic content
    // Client: render full content after mount
    if (!hasMounted) {
        return (
            <Stack
                spacing={4}
                data-testid="account-order-history-page"
                w="100%"
                maxW="100%"
                minW={0}
            >
                <Stack>
                    <Heading as="h1" fontSize="2xl">
                        <Flex alignItems="center" gap="64px" textTransform="uppercase">
                            <Box display={{base: 'inline-block', lg: 'none'}}>
                                <LeftArrow />
                            </Box>
                            <Text {...styles.OrderHistoryHeading}>
                                <FormattedMessage
                                    defaultMessage="Order History"
                                    id="account_order_history.title.order_history"
                                />
                            </Text>
                        </Flex>
                    </Heading>
                </Stack>
                <Skeleton h="42px" w="full" />
                <SkeletonPlaceholder count={8} />
            </Stack>
        )
    }

    return (
        <Stack
            spacing={6}
            data-testid="account-order-history-page"
            w="100%"
            maxW="100%"
            minW={0}
            margin="0px"
        >
            <Stack display={'flex'} flexDirection={'column'} gap={'48px'}>
                <Heading as="h1" fontSize="2xl" tabIndex="0" ref={headingRef}>
                    <Flex
                        alignItems={{base: 'start', lg: 'center'}}
                        gap={{base: '0', lg: '64px'}}
                        textTransform="uppercase"
                    >
                        <Text {...styles.OrderHistoryHeading}>
                            <FormattedMessage
                                defaultMessage="Order History"
                                id="account_order_history.title.order_history"
                            />
                        </Text>
                    </Flex>
                </Heading>
            </Stack>

            <Tabs index={activeTab} onChange={setActiveTab} variant="unstyled" isLazy>
                {/* Conditionally show TabList - hidden on mobile when no orders */}
                {showTabList &&
                    (isLoadingOnline ? (
                        <Skeleton h="42px" w="full" />
                    ) : (
                        <>
                            <TabList
                                w={{
                                    base: 'full',
                                    lg: '575px',
                                    xl: '600px',
                                    '2xl': '620px'
                                }}
                            >
                                <Tab {...styles.onlineText}>
                                    <FormattedMessage
                                        defaultMessage="Online ({count})"
                                        id="account_order_history.tab.online_orders"
                                        values={{count: totalCounts.online}}
                                    />
                                </Tab>
                                <Tab {...styles.storeText}>
                                    <FormattedMessage
                                        defaultMessage="In-Store ({count})"
                                        id="account_order_history.tab.in.store_orders"
                                        values={{count: totalCounts.store}}
                                    />
                                </Tab>
                            </TabList>
                            <TabIndicator mt="-2px" height="2px" bg="black" />
                        </>
                    ))}

                <TabPanels>
                    {/* Online Orders Tab Panel */}
                    <TabPanel
                        px={0}
                        py={onlineOrders.length > 0 || storeOrders.length > 0 ? '64px' : '26px'}
                    >
                        {isLoadingOnline ? (
                            <SkeletonPlaceholder count={8} />
                        ) : (
                            <Stack gap={0}>
                                {renderOrdersList(
                                    onlineOrders,
                                    isLoadingOnline,
                                    totalCounts.online,
                                    fetchNextOnline,
                                    isFetchingNextOnline,
                                    hasNextOnline
                                )}
                            </Stack>
                        )}

                        {/* Tab-specific empty state for online orders */}
                        {!isLoadingOnline && !totalCounts.online && (
                            <EmptyOrderMessage
                                showContinueShopping={true}
                                onlineOrders={onlineOrders}
                                storeOrders={storeOrders}
                            />
                        )}
                    </TabPanel>

                    {/* Store Orders Tab Panel */}
                    <TabPanel
                        px={0}
                         py={onlineOrders.length > 0 || storeOrders.length > 0 ? '64px' : '26px'}
                    >
                        {isLoadingStore ? (
                            <SkeletonPlaceholder />
                        ) : (
                            <Stack gap={0}>
                                {renderOrdersList(
                                    storeOrders,
                                    isLoadingStore,
                                    totalCounts.store,
                                    fetchNextStore,
                                    isFetchingNextStore,
                                    hasNextStore
                                )}
                            </Stack>
                        )}

                        {/* Tab-specific empty state for store orders */}
                        {!isLoadingStore && !totalCounts.store && (
                            <EmptyOrderMessage
                                showContinueShopping={false}
                                storeOrders={storeOrders}
                                onlineOrders={onlineOrders}
                            />
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Stack>
    )
}

AccountOrderHistory.getTemplateName = () => 'account-order-history'

export default AccountOrderHistory
